import { FC, PropsWithChildren, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';

import {
    DUPLICATE_FLOW_MODAL_ID,
    PLAYER_TYPE_MODAL_ID,
    PREFLIGHT_CHECKS_MODAL_ID,
    PREFLIGHT_FAILURE_MODAL_ID,
    RENAME_MODAL_ID,
    CONFIRM_PASSWORD_MODAL_ID,
    CREATE_REEL_MODAL_ID,
    TRIAL_EXTEND_MODAL_ID,
    AI_VIDEO_LABEL_MODAL_ID,
    PREVIEW_MODAL_ID,
    DELETE_FLOW_MODAL_ID,
    UNPUBLISH_FLOW_MODAL_ID,
    DISMISS_GET_STARTED_CHECKLIST_MODAL_ID,
    PAUSED_FLOWS_MODAL_ID,
    MAX_FLOWS_PUBLISHED_MODAL_ID,
    SUBSCRIPTION_CANCELED_MODAL_ID,
    RESET_PLAYER_DIMENSIONS_MODAL_ID,
    IMPORT_TEMPLATE_MODAL_ID,
    ARCHIVE_FLOW_MODAL_ID,
    DELETE_REEL_MODAL_ID,
    DELETE_REEL_PUBLISHED_MODAL_ID,
    DELETE_REEL_UNPUBLISHED_MODAL_ID,
    RENAME_REEL_MODAL_ID,
    TY_FOR_UPGRADE_MODAL_ID,
} from '@/types/modal.type';
import { DeleteFlowModal } from '@/Components/modal/DeleteFlowModal';
import { DuplicateFlowModal } from '@/Components/modal/DuplicateFlowModal';
import { PlayerTypeModal } from '@/Components/modal/PlayerTypeModal';
import { PreflightChecksModal } from '@/Components/modal/PreflightChecksModal';
import { PreflightFailureModal } from '@/Components/modal/PreflightFailureModal';
import { FlowRenameModal } from '@/Components/modal/FlowRenameModal';
import { ConfirmPasswordModal } from '@/Components/ConfirmPasswordModal';
import { CreateReelModal } from '@/Components/modal/CreateReelModal';
import { TrialExtendModal } from '@/Components/modal/TrialExtendModal';
import { AiVideoLabelModal } from '@/Components/modal/AiVideoLabelModal';
import { useInteractiveCallback } from '@/hook/useInteractiveCallback';
import { FlowPreviewModal } from '@/Components/modal/FlowPreviewModal';
import { DismissGetStartedChecklist } from '@/Components/modal/DismissGetStartedChecklist';
import { ResetPlayerDimensionsModal } from '@/Components/modal/ResetPlayerDimensionsModal';
import { UnpublishFlowModal } from '@/Components/modal/UnpublishFlowModal';
import { PausedFlowsModal } from '@/Components/modal/PausedFlowsModal';
import { MaxFlowsPublishedModal } from '@/Components/modal/MaxFlowsPublishedModal/MaxFlowsPublishedModal';
import { SubscriptionCanceledModal } from '@/Components/modal/SubscriptionCanceledModal/SubscriptionCanceledModal';
import { ImportTemplateModal } from '@/Components/modal/ImportTemplateModal';
import { ArchiveFlowModal } from '@/Components/modal/ArchiveFlowModal';
import { DeleteReelModal } from '@/Components/modal/DeleteReelModal/DeleteReelModal';
import { DeleteReelPublishedModal } from '@/Components/modal/DeleteReelPublishedModal/DeleteReelPublishedModal';
import { DeleteReelUnpublishedModal } from '@/Components/modal/DeleteReelUnpublishedModal/DeleteReelUnpublishedModal';
import { TyForUpgradeModal } from '@/Components/modal/TyForUpgradeModal';
import { ReelRenameModal } from '@/Components/modal/ReelRenameModal';

import { AppModalData, useAppModal } from './AppModal.context';

type AppModalProps = PropsWithChildren<{}>;

export const AppModal: FC<AppModalProps> = ({}) => {
    const { dialogElementRef, modalData, setModalData, setDialogElementRef } =
        useAppModal();

    useEffect(() => {
        if (dialogElementRef?.current) {
            dialogElementRef.current.showModal();
        }
    }, [dialogElementRef]);

    const modalId = modalData?.id;

    const modals = [
        // TODO: Remove unneeded modals
        // {
        //     id: APPEND_DICTIONARY_MODAL_ID,
        //     Component: AppendDictionaryModal,
        // },
        {
            id: DELETE_FLOW_MODAL_ID,
            Component: DeleteFlowModal,
        },
        {
            id: UNPUBLISH_FLOW_MODAL_ID,
            Component: UnpublishFlowModal,
        },
        {
            id: DUPLICATE_FLOW_MODAL_ID,
            Component: DuplicateFlowModal,
        },
        {
            id: PLAYER_TYPE_MODAL_ID,
            Component: PlayerTypeModal,
        },
        {
            id: PREFLIGHT_CHECKS_MODAL_ID,
            Component: PreflightChecksModal,
        },
        {
            id: PREFLIGHT_FAILURE_MODAL_ID,
            Component: PreflightFailureModal,
        },
        {
            id: RENAME_MODAL_ID,
            Component: FlowRenameModal,
        },
        {
            id: CONFIRM_PASSWORD_MODAL_ID,
            Component: ConfirmPasswordModal,
        },
        {
            id: CREATE_REEL_MODAL_ID,
            Component: CreateReelModal,
        },
        {
            id: TRIAL_EXTEND_MODAL_ID,
            Component: TrialExtendModal,
        },
        {
            id: AI_VIDEO_LABEL_MODAL_ID,
            Component: AiVideoLabelModal,
        },
        {
            id: PREVIEW_MODAL_ID,
            Component: FlowPreviewModal,
        },
        {
            id: DISMISS_GET_STARTED_CHECKLIST_MODAL_ID,
            Component: DismissGetStartedChecklist,
        },
        {
            id: PAUSED_FLOWS_MODAL_ID,
            Component: PausedFlowsModal,
        },
        {
            id: MAX_FLOWS_PUBLISHED_MODAL_ID,
            Component: MaxFlowsPublishedModal,
        },
        {
            id: SUBSCRIPTION_CANCELED_MODAL_ID,
            Component: SubscriptionCanceledModal,
        },
        {
            id: RESET_PLAYER_DIMENSIONS_MODAL_ID,
            Component: ResetPlayerDimensionsModal,
        },
        {
            id: IMPORT_TEMPLATE_MODAL_ID,
            Component: ImportTemplateModal,
        },
        {
            id: ARCHIVE_FLOW_MODAL_ID,
            Component: ArchiveFlowModal,
        },
        {
            id: DELETE_REEL_MODAL_ID,
            Component: DeleteReelModal,
        },
        {
            id: DELETE_REEL_PUBLISHED_MODAL_ID,
            Component: DeleteReelPublishedModal,
        },
        {
            id: DELETE_REEL_UNPUBLISHED_MODAL_ID,
            Component: DeleteReelUnpublishedModal,
        },
        {
            id: TY_FOR_UPGRADE_MODAL_ID,
            Component: TyForUpgradeModal,
        },
        {
            id: RENAME_REEL_MODAL_ID,
            Component: ReelRenameModal,
        },
    ];

    const onOpen = useInteractiveCallback((newModalData: AppModalData) => {
        onClose();

        setTimeout(() => {
            setModalData(newModalData);
        }, 100);
    });

    const onClose = useInteractiveCallback(() => {
        setModalData(null);

        dialogElementRef?.current?.close();

        setDialogElementRef(null);
    });

    return (
        <AnimatePresence>
            {modals.map(({ id, Component }) => {
                if (id === modalId) {
                    return (
                        <Component
                            key={id}
                            payload={modalData?.payload || null}
                            onClose={onClose}
                            onOpen={onOpen}
                        />
                    );
                }

                return null;
            })}
        </AnimatePresence>
    );
};
